// Product Units data layer (Phase 4, P4-WP11a) — the pack-unit hierarchy that the
// WP11 pricing spine builds on. The SINGLE interface between the app and
// public.product_units (supabase/migrations/20260721120000_product_units.sql).
//
// MODEL: each product has 1+ pack units (each / inner / case / pallet …). `factor`
// is how many BASE units are in one of this pack (each = 1, case = 24, …). Exactly
// one unit per product is the base (factor 1) and exactly one is the default sell
// unit. The DB enforces AT MOST one of each via partial-unique indexes; THIS layer
// keeps EXACTLY one by doing an unset-old-then-set-new swap, so callers never trip
// the partial-unique constraint.
//
// company_id is owned + frozen by DB triggers (inherited from the parent product);
// we never send it. Reads/writes product_units only.

import { supabase } from "./supabaseClient";

const TABLE = "product_units";
const SELECT = "id, product_id, code, name, factor, is_base, is_default_sell, barcode";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}

function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23505": message = "A unit with that code already exists for this product."; break;
    case "23514": message = "Factor must be greater than 0."; break;
    case "23502": message = "Unit code is required."; break;
    case "23503": message = "That product does not exist."; break;
    case "42501": message = "You do not have access to this product."; break;
    case "PGRST116": message = "Unit not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

// Normalise a numeric factor: blank/absent → 1 (base default); otherwise the number.
const toFactor = (v) => (v == null || v === "" ? 1 : Number(v));

// Set a sole flag (is_base / is_default_sell) to false on every OTHER unit of the
// product, so the caller can then set it true on one row without a partial-unique clash.
async function unsetFlag(c, productId, column, exceptId) {
  let q = c.from(TABLE).update({ [column]: false }).eq("product_id", productId).eq(column, true);
  if (exceptId) q = q.neq("id", exceptId);
  const { error } = await q;
  if (error) throw toError(error, "Could not update units");
}

/** A product's units, ordered base-first then by factor ascending. */
export async function listUnits(productId) {
  const { data, error } = await client().from(TABLE).select(SELECT)
    .eq("product_id", productId)
    .order("is_base", { ascending: false })
    .order("factor", { ascending: true });
  if (error) throw toError(error, "Could not load units");
  return data || [];
}

/**
 * Add a unit to a product. The product's FIRST unit is forced to be the base +
 * default-sell (a product must always have exactly one of each); an explicit
 * is_base / is_default_sell swaps the flag off any sibling first.
 */
export async function createUnit(productId, payload = {}) {
  const c = client();
  if (!payload.code || !String(payload.code).trim()) throw new Error("Unit code is required.");

  const existing = await listUnits(productId);
  const is_base = !!payload.is_base || !existing.some((u) => u.is_base);
  const is_default_sell = !!payload.is_default_sell || !existing.some((u) => u.is_default_sell);

  if (is_base) await unsetFlag(c, productId, "is_base");
  if (is_default_sell) await unsetFlag(c, productId, "is_default_sell");

  const row = {
    product_id: productId,
    code: String(payload.code).trim(),
    name: payload.name || null,
    factor: toFactor(payload.factor),
    barcode: payload.barcode || null,
    is_base,
    is_default_sell,
  };
  const { data, error } = await c.from(TABLE).insert(row).select("id").single();
  if (error) throw toError(error, "Could not add unit");
  return data.id;
}

/**
 * Update a unit. Turning is_base / is_default_sell ON swaps it off any sibling first;
 * turning the last one OFF is blocked (a product must keep one of each — promote
 * another unit instead).
 */
export async function updateUnit(id, patch = {}) {
  const c = client();
  const { data: cur, error: e0 } = await c.from(TABLE).select("product_id, is_base, is_default_sell").eq("id", id).single();
  if (e0) throw toError(e0, "Unit not found");
  const productId = cur.product_id;

  if (patch.is_base === true && !cur.is_base) await unsetFlag(c, productId, "is_base", id);
  if (patch.is_base === false && cur.is_base) throw new Error("A product must keep one base unit — set another unit as the base instead.");
  if (patch.is_default_sell === true && !cur.is_default_sell) await unsetFlag(c, productId, "is_default_sell", id);
  if (patch.is_default_sell === false && cur.is_default_sell) throw new Error("A product must keep one default-sell unit — set another unit as the default instead.");

  const update = {};
  if (patch.code !== undefined) update.code = String(patch.code).trim();
  if (patch.name !== undefined) update.name = patch.name || null;
  if (patch.factor !== undefined) update.factor = toFactor(patch.factor);
  if (patch.barcode !== undefined) update.barcode = patch.barcode || null;
  if (patch.is_base !== undefined) update.is_base = patch.is_base;
  if (patch.is_default_sell !== undefined) update.is_default_sell = patch.is_default_sell;

  const { error } = await c.from(TABLE).update(update).eq("id", id).select("id").single();
  if (error) throw toError(error, "Could not update unit");
  return id;
}

/**
 * Delete a unit. If it was the base / default-sell and siblings remain, the sole
 * role is re-homed to the smallest-factor sibling so the product keeps exactly one
 * of each. Deleting a product's last unit simply leaves it with none.
 */
export async function deleteUnit(id) {
  const c = client();
  const { data: cur, error: e0 } = await c.from(TABLE).select("product_id, is_base, is_default_sell").eq("id", id).single();
  if (e0) throw toError(e0, "Unit not found");

  const { error } = await c.from(TABLE).delete().eq("id", id);
  if (error) throw toError(error, "Could not delete unit");

  if (cur.is_base || cur.is_default_sell) {
    const remaining = await listUnits(cur.product_id);   // base-first, then factor ascending
    if (remaining.length) {
      const patch = {};
      if (cur.is_base && !remaining.some((u) => u.is_base)) patch.is_base = true;
      if (cur.is_default_sell && !remaining.some((u) => u.is_default_sell)) patch.is_default_sell = true;
      if (Object.keys(patch).length) {
        const { error: e2 } = await c.from(TABLE).update(patch).eq("id", remaining[0].id).select("id").single();
        if (e2) throw toError(e2, "Could not reassign the unit role");
      }
    }
  }
}
